import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { Toggle } from '../../../../web/components/Toggle';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { brandPrimaryLight, grayIconFill } from '../../../styles/colors';
import { LeadRuleViewModel, RecurrenceType, TurnThisOnOptions } from '../../../viewModels/leadrules';
import { TransparentButton } from '../../TransparentButton';
import { Fill } from '../../leadView/components/Fill';
import { Group } from '../../leadView/components/Group';
import { styleSheet } from '../styles';
import {
	findDurationOption,
	hourOptions,
	numberRange,
	prepareFromWeeklySchedule,
	prepareToWeeklySchedule,
	turnThisOnOptions,
} from '../utils';
import { CustomDurationPicker } from './CustomDurationPicker';
import { IScheduleInterval, ScheduleModal } from './ScheduleModal';
import { SetCustomValueModal } from './SetCustomValueModal';

interface IProps {
	leadRule: LeadRuleViewModel;
}

export const generateCustomValueOption = (value: number): ISelectOption<number> => ({
	dataContext: value,
	id: 'custom-value',
	text: String(value),
});

export const getSelectedOption = (leadRulePropValue: number, options: ISelectOption<number>[] = numberRange) => {
	const defaultOption = options.find(item => item.id === 'default');
	const existentOption = options.find(item => item.dataContext === leadRulePropValue);
	const selected = existentOption
		? existentOption
		: leadRulePropValue
			? generateCustomValueOption(leadRulePropValue)
			: defaultOption;
	return selected as ISelectOption<number>;
};

export const RuleContainerHeader: React.FC<IProps> = observer(({ leadRule }) => {
	const defaultTurnThisOn = turnThisOnOptions[0];
	const defaultDuration = hourOptions[0];
	const [selectedTurnThisOn, setTurnThisOn] = useState<ISelectOption<number>>(defaultTurnThisOn);
	const [selectedDuration, setDuration] =
		useState<ISelectOption<{ value: number; unit: 'hours' | 'days' } | number | null>>(defaultDuration);

	const [scheduleModal, setScheduleModal] = useState(false);
	const [schedule, setSchedule] = useState(null);

	const [selectedMaxPerHr, setMaxPerHr] = useState(null);
	const [selectedMaxPerDay, setMaxPerDay] = useState(null);

	const [showCustomPicker, setShowCustomPicker] = useState(false);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

	const secondaryTurnThisOn = turnThisOnOptions[1];

	useEffect(() => {
		if (leadRule?.recurrence === RecurrenceType.AlwaysOn) {
			setTurnThisOn(defaultTurnThisOn);
			setDuration(defaultDuration);
		}
		if (leadRule?.recurrence === RecurrenceType.SingleInterval) {
			// Calculate duration from schedule timestamps
			const ruleSchedule = leadRule?.schedule;
			if (ruleSchedule?.max && ruleSchedule?.min) {
				const durationOption = findDurationOption(ruleSchedule.min, ruleSchedule.max);

				setTurnThisOn(defaultTurnThisOn);
				if (durationOption) {
					setDuration(durationOption);
				} else {
					// No matching preset option, treat as custom
					const endDate = moment(ruleSchedule.max).toDate();
					setCustomEndDate(endDate);
					setDuration(generateCustomDateOption(endDate));
				}
			}
		}
		if (leadRule?.recurrence === RecurrenceType.Weekly) {
			setTurnThisOn(secondaryTurnThisOn);
			setDuration(defaultDuration);
			const prepared = [...(leadRule?.weeklySchedule ?? [])].map(prepareFromWeeklySchedule);

			setSchedule(prepared);
		}

		setMaxPerDay(getSelectedOption(leadRule?.dailyLimit));

		setMaxPerHr(getSelectedOption(leadRule?.hourlyLimit));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadRule]);

	useEffect(() => {
		if (!schedule) {
			setTurnThisOn(defaultTurnThisOn);
		} else {
			setTurnThisOn(secondaryTurnThisOn);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schedule]);

	const handleEnable = () => {
		leadRule?.setEnabled(!leadRule?.isEnabled);
	};

	const handleTurnThisOn = (option: ISelectOption<number>) => {
		setTurnThisOn(option);
		if (option.id === TurnThisOnOptions.Scheduled) {
			setScheduleModal(true);
		} else {
			handleDuration(defaultDuration);
			leadRule?.setSchedule(null);
			leadRule?.setRecurrence(RecurrenceType.AlwaysOn);
		}
	};

	const handleDuration = (option: ISelectOption<{ value: number; unit: 'hours' | 'days' } | number | null>) => {
		if (option.id === RecurrenceType.AlwaysOn) {
			leadRule?.setSchedule(null);
			leadRule?.setRecurrence(RecurrenceType.AlwaysOn);
			setDuration(option);
		} else if (option.id === 'custom') {
			setShowCustomPicker(true);
			// Don't set duration yet, wait for user to pick date
		} else {
			leadRule?.setSchedule(option.dataContext);
			leadRule?.setRecurrence(RecurrenceType.SingleInterval);
			setDuration(option);
		}

		leadRule?.setWeeklySchedule(null);
	};

	const generateCustomDateOption = (date: Date): ISelectOption<null> => ({
		dataContext: null,
		id: 'custom-date',
		text: `Exp ${moment(date).format('M/D, h:mm a')}`,
	});

	const handleCustomDateSave = (endDate: Date) => {
		leadRule?.setScheduleByDate(endDate);
		leadRule?.setRecurrence(RecurrenceType.SingleInterval);
		setCustomEndDate(endDate);
		setDuration(generateCustomDateOption(endDate));
		setShowCustomPicker(false);
	};

	const handleCustomDateCancel = () => {
		setShowCustomPicker(false);
		// If no custom date was previously set, revert to default
		if (!customEndDate) {
			setDuration(defaultDuration);
		}
	};

	const handleMaxPerHr = (option: ISelectOption<number>) => {
		if (option.id !== 'custom') {
			leadRule.setHourlyLimit(option.dataContext);

			setMaxPerHr(option);
			return;
		}

		setMaxPerHr(option);
	};

	const handleMaxPerDay = (option: ISelectOption<number>) => {
		if (option.id !== 'custom') {
			leadRule.setDailyLimit(option.dataContext);

			setMaxPerDay(option);
			return;
		}

		setMaxPerDay(option);
	};

	const handleModalClose = () => {
		setScheduleModal(false);
		if (!schedule) {
			setTurnThisOn(defaultTurnThisOn);
		}
	};

	const handleScheduleSave = (savedSchedule: IScheduleInterval[]) => {
		setSchedule(savedSchedule);
		setScheduleModal(false);
		const prepared = [...savedSchedule].map(prepareToWeeklySchedule);
		leadRule?.setRecurrence(RecurrenceType.Weekly);
		leadRule?.setSchedule(null);

		leadRule?.setWeeklySchedule(prepared);
	};

	const handleEditSchedule = () => setScheduleModal(true);

	const showDuration = selectedTurnThisOn?.id === TurnThisOnOptions.RightAway;

	const isScheduled = selectedTurnThisOn?.id === TurnThisOnOptions.Scheduled;

	return (
		<div className={css(styleSheet.ruleContainerHeader)}>
			<Group vCentered={true}>
				<div className={css(styleSheet.regularText)}>Turn on:</div>
				<Select
					value={selectedTurnThisOn?.id}
					onValueChange={id => {
						const option = turnThisOnOptions.find(o => o.id === id);
						if (option) handleTurnThisOn(option);
					}}
				>
					<SelectTrigger styles={styleSheet.regularSelect}>
						<span>{selectedTurnThisOn?.text ?? 'Select...'}</span>
					</SelectTrigger>
					<SelectContent>
						{turnThisOnOptions.map(option => (
							<SelectItem key={option.id} value={option.id}>
								{option.text}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{showDuration && (
					<>
						<div className={css(styleSheet.regularText)}>Duration:</div>
						<Select
							value={selectedDuration?.id}
							onValueChange={id => {
								const option = hourOptions.find(o => o.id === id);
								if (option) handleDuration(option);
							}}
						>
							<SelectTrigger styles={styleSheet.regularSelect}>
								<span>{selectedDuration?.text ?? 'Select...'}</span>
							</SelectTrigger>
							<SelectContent>
								{hourOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				)}

				{schedule && isScheduled && (
					<TransparentButton onClick={handleEditSchedule}>
						<Group spacingSize={5}>
							<EditItemPenIcon fillColor={brandPrimaryLight} />
							<div className={css(styleSheet.regularText, styleSheet.disabled)}>Edit schedule</div>
						</Group>
					</TransparentButton>
				)}

				<div className={css(styleSheet.regularText)}>Max leads/hr:</div>
				<div className={css(styleSheet.fieldWrapper)}>
					<Select
						value={selectedMaxPerHr?.id}
						onValueChange={id => {
							const option = numberRange.find(o => o.id === id);
							if (option) handleMaxPerHr(option);
						}}
					>
						<SelectTrigger styles={styleSheet.timeSelect}>
							<span>{selectedMaxPerHr?.text ?? 'Select...'}</span>
						</SelectTrigger>
						<SelectContent>
							{numberRange.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					{selectedMaxPerHr?.id === 'custom' ? (
						<SetCustomValueModal
							onClickToClose={() => setMaxPerHr(getSelectedOption(leadRule?.hourlyLimit))}
							onClickToSave={value => {
								setMaxPerHr(generateCustomValueOption(Number(value)));
								leadRule.setHourlyLimit(Number(value));
							}}
						/>
					) : null}
				</div>
				<div className={css(styleSheet.regularText)}>Max leads/day:</div>
				<div className={css(styleSheet.fieldWrapper)}>
					<Select
						value={selectedMaxPerDay?.id}
						onValueChange={id => {
							const option = numberRange.find(o => o.id === id);
							if (option) handleMaxPerDay(option);
						}}
					>
						<SelectTrigger styles={styleSheet.timeSelect}>
							<span>{selectedMaxPerDay?.text ?? 'Select...'}</span>
						</SelectTrigger>
						<SelectContent>
							{numberRange.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					{selectedMaxPerDay?.id === 'custom' ? (
						<SetCustomValueModal
							onClickToClose={() => setMaxPerDay(getSelectedOption(leadRule?.dailyLimit))}
							onClickToSave={value => {
								setMaxPerDay(generateCustomValueOption(Number(value)));
								leadRule.setDailyLimit(Number(value));
							}}
						/>
					) : null}
				</div>

				<Fill />

				<div className={css(styleSheet.fieldWrapper)}>
					<span className={css(styleSheet.regularText)}>Enabled:</span>
					<Toggle
						id={`${leadRule?.id}-modal-toggle`}
						isOn={leadRule?.isEnabled}
						onToggleCheckChanged={handleEnable}
						uncheckedColor={grayIconFill}
					/>
				</div>
			</Group>
			<ScheduleModal
				values={schedule}
				isOpen={scheduleModal}
				onClickToClose={handleModalClose}
				onClickToSave={handleScheduleSave}
			/>
			<CustomDurationPicker
				initialValue={customEndDate}
				isOpen={showCustomPicker}
				onCancel={handleCustomDateCancel}
				onSave={handleCustomDateSave}
			/>
		</div>
	);
});
